import { Expose, Type, Exclude, classToPlain } from 'class-transformer';
import { Feature, Point } from 'geojson';

import { ExposeRawData } from '@lib/common/decorators';
import { SnModel, SnCollection } from '@lib/common/models';

import { SnIncidentMedia } from './incidents/incident-media';
import { SnRecording, SnRecordingUrls } from './recording';
import { SnTeam } from './team';
import { SnTip } from './tip';
import { SnUser } from './user';

export class SnIncident extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnTip)
  public tips: SnCollection<SnTip>;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnRecording)
  public recordings: SnCollection<SnRecording>;

  @Expose()
  public description: string;

  @Expose()
  public caseNumber: string;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public publicToken: number;

  @Expose()
  public address: string;

  @Expose()
  public city: string;

  @Expose()
  public state: string;

  @Expose()
  public zip: string;

  @Expose()
  public media: SnIncidentMedia[];

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnTeam)
  public organization: SnTeam;

  @Expose()
  @ExposeRawData()
  public geometry: Point;

  @ExposeRawData()
  public integrationMeta: any;

  @Expose({ name: 'usngCoord' })
  @Exclude({ toPlainOnly: true })
  public usngCoordinates: string;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public speed: number;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public altitude: number;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public urls: SnRecordingUrls;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public durationSeconds: number;

  @Expose()
  @Exclude({ toPlainOnly: true })
  public fileIsAvailable: boolean;

  @Expose()
  @Exclude({ toPlainOnly: true })
  @Type(() => SnUser)
  public user: SnUser;

  @Expose({ name: 'createdTimestamp' })
  @Type(() => Date)
  public createdTimestamp: Date;

  @Expose({ name: 'updatedTimestamp' })
  @Type(() => Date)
  public updatedTimestamp: Date;

  constructor(data: any = {}) {
    super();
    this.fromJSON(data);
  }

  get coords(): { lng: number; lat: number } {
    return {
      lng: this.geometry.coordinates[0],
      lat: this.geometry.coordinates[1],
    };
  }

  get fullAddress(): string {
    return `${this.address} ${this.city}, ${this.state} ${this.zip}`;
  }

  public toGeoJSON(): Feature<Point> {
    return {
      type: 'Feature',
      geometry: this.geometry,
      id: this.id,
      properties: {
        id: this.id,
      },
    };
  }

  public toMetaJSON(): Object {
    return {
      latitude: this.coords.lat,
      longitude: this.coords.lng,
      ...classToPlain(this),
    };
  }
}
